import type React from 'react';
import { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { Menu, MenuButton, MenuItem, MenuProvider } from '@ariakit/react/menu';
import { useObservableState } from 'observable-hooks';
import { getGlobalForceMenuOpenById$ } from '../../../../menu';
import classNames from '../../../../utils/classNames';
import Button from '../../../form/Button';
import SkeletonLoader from '../../../skeletonLoader/SkeletonLoader';
import { ROW_ICON_BUTTON_CLASSES } from './ProjectMeasuringPointRow/cellClasses';

type MenuItemTarget =
    | { onClick: () => void; to?: never; href?: never }
    /** A page inside the single-page app, opened through React Router. */
    | { to: string; openInNewTab?: boolean; onClick?: never; href?: never }
    /** A page outside the single-page app, or a file. */
    | { href: string; onClick?: never; to?: never }
    | { onClick?: never; to?: never; href?: never };

export type OptionsMenuItem = {
    label: string;
    disabled?: boolean;
    id?: string;
} & MenuItemTarget;

function renderMenuItemElement(item: OptionsMenuItem): React.ReactElement {
    if (item.to !== undefined) {
        return (
            <Button
                variant="base"
                to={item.to}
                {...(item.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            />
        );
    }
    if (item.href !== undefined) {
        return <Button variant="base" href={item.href} />;
    }
    return <Button variant="base" type="button" />;
}

interface OptionsMenuProps {
    items: OptionsMenuItem[];
    ariaLabel: string;
    onOpenChange?: (open: boolean) => void;
    calibrationSkeletonCount?: number;
}

export function OptionsMenu({
    items,
    ariaLabel,
    onOpenChange,
    calibrationSkeletonCount = 0,
}: OptionsMenuProps): React.ReactElement {
    const [open, setOpen] = useState(false);
    // A walkthrough keeps this menu open by the id of one of its items, because its overlay takes
    // the pointer away from the menu.
    const forcedOpenId = useObservableState(getGlobalForceMenuOpenById$());
    const isForcedOpen =
        forcedOpenId !== undefined && items.some((item) => item.id === forcedOpenId);

    function stopRowTogglePropagation(event: React.SyntheticEvent) {
        event.stopPropagation();
    }

    const setOpenCallback = useCallback(
        (nextOpen: boolean) => {
            setOpen(nextOpen);
            onOpenChange?.(nextOpen);
        },
        [onOpenChange]
    );

    if (!items.length) {
        return <div className="inline-block h-7 w-7" aria-hidden="true" />;
    }

    const menuItemsClasses =
        'border-menu-border px-2.5 py-2.5 block w-full border border-b-0 text-form-text hover:underline data-[active-item]:underline';
    const menuItemsDisabledClasses =
        'opacity-55 cursor-not-allowed no-underline hover:no-underline';

    const baseMenuClasses =
        'z-floating-menu w-52 border-menu-border shadow-floating-menu block border-b bg-main-background text-center';

    return (
        <MenuProvider
            open={open || isForcedOpen}
            setOpen={setOpenCallback}
            placement="right-start"
            showTimeout={100}
            hideTimeout={0}
        >
            <div className="relative inline-block">
                <MenuButton
                    showOnHover
                    className={ROW_ICON_BUTTON_CLASSES}
                    aria-label={ariaLabel}
                    onClick={stopRowTogglePropagation}
                    onMouseDown={stopRowTogglePropagation}
                >
                    <FontAwesomeIcon icon={faCog} aria-hidden />
                </MenuButton>

                <Menu
                    portal
                    gutter={8}
                    overflowPadding={8}
                    hideOnHoverOutside
                    // The menu is rendered in a portal, outside the table row subtree.
                    // Mark it as a no-toggle zone so row expand/collapse handlers can
                    // ignore these clicks and prevent click-through row toggles.
                    data-stop-row-toggle="true"
                    onClick={stopRowTogglePropagation}
                    onMouseDown={stopRowTogglePropagation}
                    className={baseMenuClasses}
                >
                    {items.map((item, index) =>
                        item.disabled ? (
                            <MenuItem
                                key={`${item.label}-${index}`}
                                disabled
                                accessibleWhenDisabled
                                className={classNames(menuItemsClasses, menuItemsDisabledClasses)}
                            >
                                {item.label}
                            </MenuItem>
                        ) : (
                            <MenuItem
                                key={`${item.label}-${index}`}
                                id={item.id}
                                className={menuItemsClasses}
                                render={renderMenuItemElement(item)}
                                onClick={
                                    item.onClick &&
                                    ((event: React.MouseEvent<HTMLElement>) => {
                                        event.stopPropagation();
                                        item.onClick();
                                    })
                                }
                            >
                                {item.label}
                            </MenuItem>
                        )
                    )}

                    {calibrationSkeletonCount > 0 &&
                        Array.from({ length: calibrationSkeletonCount }, (_, i) => (
                            <div
                                key={`calibration-skeleton-${i}`}
                                className={menuItemsClasses}
                                role="presentation"
                                aria-hidden="true"
                            >
                                <SkeletonLoader count={1} />
                            </div>
                        ))}
                </Menu>
            </div>
        </MenuProvider>
    );
}
